/**
 * Where a third-party round trip — OAuth sign-in, Stripe checkout, the billing portal — lands when
 * it was started from the desktop app, instead of the deployment's own web origin (I-1078, ADR-160).
 *
 * `desktop/src-tauri` registers `featit-return` with the OS the way a mail client registers
 * `mailto:` — nothing else on the machine answers it. A backend that decided a round trip started
 * from the desktop app hands the browser one of these addresses instead of the deployment's own
 * origin, on every outcome; the OS hands it back to the desktop app, which reads it client-side,
 * through the same routes a web return already uses.
 *
 * ⛔ **Not a credential, and not read as one.** Every value a `featit-return` link carries is
 * already safe to see in a browser's address bar on the web path it mirrors: a one-time, 60-second
 * sign-in exchange code that still has to be redeemed over `POST /auth/oauth/exchange`
 * (`oauth.service.ts`'s class comment — "never a token"), or a plain billing outcome. A link minted
 * from anywhere else — pasted into a browser, forwarded in an email — carries either no code or an
 * expired or already-spent one, or — if it carries a live code someone else started — a code bound
 * to the verifier of the app that started that sign-in, which the app it was thrown at does not
 * hold (I-1182, ADR-160); it cannot sign anybody in by itself (I-1078's "Done when").
 *
 * ⛔ **Not a `CredentialAudienceType`.** `credentials.ts`'s `CREDENTIAL_AUDIENCES` deliberately has
 * no `'desktop'` entry — "an audience that names a shell instead of a boundary is one more value to
 * check without one more thing being checked." A sign-in started from the desktop app still mints a
 * credential with `audience: 'web'`; which address the browser lands on afterwards is a routing
 * decision, carried separately (`desktopReturn`, not `audience`), never folded into it.
 *
 * ⚠️ **A different mechanism from the passkey sheet's `featit-passkey` (I-0775).** That scheme is
 * intercepted by `ASWebAuthenticationSession` itself and never reaches the OS's general
 * URL-scheme registry or a system browser tab. `featit-return` is the opposite case — registered
 * with the OS precisely so a browser *outside* any sheet can hand off to it — so the two schemes
 * are kept apart on purpose rather than sharing one that two different code paths would have to
 * agree on the meaning of.
 */

import { appPath, parseAddress } from '../addressing/paths.js';
import { AGENT_START_MAC_SECTION_ANCHOR } from '../agent-start-mac-place.js';
import {
  parseRepositoryFileOpenSearch,
  parseRepositoryFilePlaceSearch,
  repositoryFileDirectAddress,
  repositoryOpenPathProblem,
  REPOSITORY_FILE_OPEN_PATH,
} from '../repository-file/open-address.js';

/** The scheme the desktop app alone registers with the OS, and nothing else answers. */
export const DESKTOP_RETURN_SCHEME = 'featit-return';

/**
 * The scheme the desktop QA build registers instead (T-0561): a built app is QA'd beside the
 * installed one, and two apps answering one scheme means the OS picks, so the QA build owns its own.
 * The backend mints a link for it only when the request names it; nothing else is accepted.
 */
export const DESKTOP_QA_RETURN_SCHEME = 'featit-return-qa';

/** Every scheme a backend may mint a desktop-return link for. */
export const DESKTOP_RETURN_SCHEMES: readonly string[] = [
  DESKTOP_RETURN_SCHEME,
  DESKTOP_QA_RETURN_SCHEME,
];

/** The address a desktop-return link for `scheme` starts with; an unknown or absent scheme is the shipped one. */
export function desktopReturnOriginFor(scheme?: unknown): string {
  const known = typeof scheme === 'string' && DESKTOP_RETURN_SCHEMES.includes(scheme);
  return `${known ? scheme : DESKTOP_RETURN_SCHEME}://${DESKTOP_RETURN_HOST}`;
}

/** The authority every desktop-return link carries. Arbitrary, fixed, and never read for a decision. */
const DESKTOP_RETURN_HOST = 'app';

/** `featit-return://app` — every desktop-return address is this plus an app-relative path. */
export const DESKTOP_RETURN_ORIGIN = `${DESKTOP_RETURN_SCHEME}://${DESKTOP_RETURN_HOST}`;

/**
 * An app-relative path made safe to append to {@link DESKTOP_RETURN_ORIGIN}: exactly one leading
 * `/`, never `//`. The same rule `oauth.service.ts`'s `safeRedirectPath` already applies to a path
 * that has crossed a third party and come back — a protocol-relative value is how an open redirect
 * smuggles a second host through what reads as a path, and this value has crossed one too.
 */
function safeAppPath(path: string): string {
  return path.startsWith('/') && !path.startsWith('//') ? path : '/';
}

/**
 * The desktop-return address for an app-relative path — `appPath(...)`'s own output, or a bare
 * route such as `/login`, with whatever query the caller already attached.
 */
export function desktopReturnUrl(path: string, scheme?: unknown): string {
  return `${desktopReturnOriginFor(scheme)}${safeAppPath(path)}`;
}

/**
 * Whether `url` is one of this app's own desktop-return links.
 *
 * ⛔ Checked by exact scheme **and** host: a URL of some other scheme, or of this scheme with a
 * different host, is never one of this app's own addresses, however it reads at a glance — nothing
 * here ever minted it, so nothing here should route by it.
 */
export function isDesktopReturnUrl(url: string): boolean {
  return DESKTOP_RETURN_SCHEMES.some((scheme) => {
    const origin = desktopReturnOriginFor(scheme);
    return url === origin || url.startsWith(`${origin}/`);
  });
}

/**
 * The destinations a desktop-return link may name (I-1182), each with the only query keys it reads.
 * Exactly what the backend mints: the sign-in landing, the profile after connecting a provider,
 * and the billing outcome. `/login`'s `desktopChallenge` names the sign-in its code belongs to, so
 * the app spends its kept verifier only on its own return (I-1437, ADR-160 AC-31). Anything else — a link to `/-/settings`, to a work area, to a path some
 * page invented — is refused rather than routed: this scheme is answered by whatever page the
 * system browser opened, and a route it may name is a route anybody can make the app navigate to.
 */
const DESKTOP_RETURN_DESTINATIONS: ReadonlyArray<{ path: string; keys: readonly string[] }> = [
  { path: '/login', keys: ['oauthCode', 'desktopChallenge', 'oauthError', 'redirect'] },
  { path: appPath('/profile'), keys: ['oauthError', 'oauthLinked'] },
  { path: appPath('/settings/billing'), keys: ['namespace', 'checkout'] },
];

/**
 * I-1914: the one place a page in a browser may open in the Mac app — Infrastructure → This machine
 * at Starting agent sessions, where a refused Start session's missing step is. Matched as the whole
 * rest of the link, byte for byte: no query, no other anchor, no pattern, no `..`. It only navigates
 * to a settings section the person sees anyway; changing anything there still asks on the Mac
 * (Touch ID for a widening). Kept apart from {@link DESKTOP_RETURN_DESTINATIONS}, whose entries
 * read query keys, so this one can never grow one.
 */
export const DESKTOP_RETURN_START_SETTINGS_PATH = `${appPath('/infrastructure')}#${AGENT_START_MAC_SECTION_ANCHOR}`;

/** The desktop-return link that opens Starting agent sessions in the Mac app (I-1914). */
export const DESKTOP_RETURN_START_SETTINGS_URL = `${DESKTOP_RETURN_ORIGIN}${DESKTOP_RETURN_START_SETTINGS_PATH}`;

/**
 * I-2357 OSP-74g (D-0458 b): "Open in the FEATit app" on a file a browser could not read hands the
 * Mac app F-0766's documented lookup address, `/-/open/file`, which the app reads through the same
 * lookup page. Its query is the four keys that page reads and nothing else; each is checked by
 * `parseRepositoryFileOpenSearch`, as the page checks it. Unlike {@link DESKTOP_RETURN_DESTINATIONS},
 * nothing is dropped: a key the page does not read, a key named twice or a query that does not parse
 * refuses the whole link, and the path must be `/-/open/file` byte for byte (no `..`, no encoding).
 */
const DESKTOP_RETURN_FILE_OPEN_KEYS: readonly string[] = ['remote', 'path', 'at', 'line'];

function desktopReturnFileOpenPath(rest: string): string | null {
  const queryAt = rest.indexOf('?');
  if ((queryAt < 0 ? rest : rest.slice(0, queryAt)) !== REPOSITORY_FILE_OPEN_PATH) return null;
  if (rest.includes('#')) return null;
  const query = new URLSearchParams(queryAt < 0 ? '' : rest.slice(queryAt + 1));
  const keys = [...query.keys()];
  if (keys.some((key) => !DESKTOP_RETURN_FILE_OPEN_KEYS.includes(key))) return null;
  if (new Set(keys).size !== keys.length) return null;
  if (!parseRepositoryFileOpenSearch(query).ok) return null;
  const kept = new URLSearchParams();
  for (const key of DESKTOP_RETURN_FILE_OPEN_KEYS) {
    const value = query.get(key);
    if (value) kept.set(key, value);
  }
  return `${REPOSITORY_FILE_OPEN_PATH}?${kept.toString()}`;
}

/** The only keys the direct file address reads (F-0766 §4.1). */
const DESKTOP_RETURN_FILE_DIRECT_KEYS: readonly string[] = ['at', 'line'];

/**
 * I-2357 OSP-74g: the file page's own address in its work area, `/<namespace>/<work area>/-/files/<path>`
 * with `at` and `line` (F-0766 §4.1), for a work area that names no repository, where the lookup
 * address has no remote to name. Read by the address parser (`parseAddress`): a work area, its
 * `/files/` route, a path the lookup would take, and only `at` and `line`, each checked. Then the link
 * must be byte for byte the address `repositoryFileDirectAddress` writes for what it names, so a
 * folder address, a bare or legacy reference, a doubled slash, an encoded slash or `..`, another
 * spelling of a segment, another key, a repeated key and a fragment are all refused.
 */
function desktopReturnFileDirectPath(rest: string): string | null {
  if (rest.includes('#')) return null;
  const queryAt = rest.indexOf('?');
  let address: ReturnType<typeof parseAddress>;
  let path: string;
  try {
    address = parseAddress(queryAt < 0 ? rest : rest.slice(0, queryAt));
    if (address.kind !== 'work-area' || !address.route.startsWith('/files/')) return null;
    path = address.route.slice('/files/'.length).split('/').map(decodeURIComponent).join('/');
  } catch {
    return null;
  }
  if (repositoryOpenPathProblem(path)) return null;
  const query = new URLSearchParams(queryAt < 0 ? '' : rest.slice(queryAt + 1));
  const keys = [...query.keys()];
  if (keys.some((key) => !DESKTOP_RETURN_FILE_DIRECT_KEYS.includes(key))) return null;
  if (new Set(keys).size !== keys.length) return null;
  const place = parseRepositoryFilePlaceSearch(query);
  if (!place.ok) return null;
  const canonical = repositoryFileDirectAddress(address.reference, path, place);
  return canonical === rest ? canonical : null;
}

/**
 * The app-relative path and query a desktop-return link carries, or `null` for anything that is not
 * one — including a well-formed `featit-return` URL with a different authority, which this app
 * never minted and must not be trusted to route anywhere, and a path that is not on
 * {@link DESKTOP_RETURN_DESTINATIONS}, the I-1914 settings section, the repository file lookup or a
 * work area's file address (OSP-74g). Query keys a destination does not read are dropped, and a `redirect` that is not a
 * same-app path is dropped with them; the file lookup instead refuses a key it does not read.
 */
export function desktopReturnPath(url: string): string | null {
  if (!isDesktopReturnUrl(url)) return null;
  const rest = url.slice(url.indexOf('://') + `://${DESKTOP_RETURN_HOST}`.length);
  // ⛔ `new URL('//host/login', base)` reads `host` as the authority and `/login` as the path, so a
  // protocol-relative or backslashed rest is refused before it is parsed, not after.
  if (!rest.startsWith('/') || rest.startsWith('//') || rest.includes('\\')) return null;
  if (rest === DESKTOP_RETURN_START_SETTINGS_PATH) return DESKTOP_RETURN_START_SETTINGS_PATH;
  if (rest.startsWith(`${REPOSITORY_FILE_OPEN_PATH}?`) || rest === REPOSITORY_FILE_OPEN_PATH)
    return desktopReturnFileOpenPath(rest);
  // OSP-74g: a work area's file address; anything else under a work area is refused below.
  if (rest.split(/[?#]/)[0].includes('/-/files/')) return desktopReturnFileDirectPath(rest);
  let parsed: URL;
  try {
    parsed = new URL(rest, 'http://desktop-return.invalid');
  } catch {
    return null;
  }
  const destination = DESKTOP_RETURN_DESTINATIONS.find((entry) => entry.path === parsed.pathname);
  if (!destination) return null;
  const kept = new URLSearchParams();
  for (const key of destination.keys) {
    const value = parsed.searchParams.get(key);
    if (!value) continue;
    if (key === 'redirect' && (!value.startsWith('/') || value.startsWith('//'))) continue;
    kept.set(key, value);
  }
  const query = kept.toString();
  return query ? `${destination.path}?${query}` : destination.path;
}
